import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Building2,
  Package,
  ShoppingCart,
  Clock,
  FileText,
  Wallet,
  ArrowRight,
  Bell,
  Menu,
  X,
  User,
  LogOut,
  ChevronRight,
  Activity,
  CheckCircle,
  AlertCircle,
  Mail,
  Phone,
  MapPin,
  Edit,
  Eye,
  MessageSquare,
  Calendar,
  Upload,
  CreditCard,
  FileCheck,
  Trash2,
} from "lucide-react";
import Card from "../modern-components/ui/Card";
import Button from "../modern-components/ui/Button";
import Badge from "../modern-components/ui/Badge";
import {
  quoteAPI,
  orderAPI,
  financeAPI,
  notificationAPI,
} from "../services/api";

const Home = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("dashboard"); // dashboard, profile, quotes, orders
  const [quotes, setQuotes] = useState([]);
  const [quotesLoading, setQuotesLoading] = useState(false);
  const [quotesError, setQuotesError] = useState(null);
  const [quoteDetailModal, setQuoteDetailModal] = useState(null); // { quote, response }
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState(null);

  // Finance state
  const [financeDocuments, setFinanceDocuments] = useState({
    bankStatement: null,
    gstCertificate: null,
    udyamCertificate: null,
    panCard: null,
  });
  const [financeStatus, setFinanceStatus] = useState(null); // null, 'pending', 'submitted', 'verified'
  const [financeLoading, setFinanceLoading] = useState(false);
  const [financeError, setFinanceError] = useState(null);
  const [financeSuccess, setFinanceSuccess] = useState(false);

  // Dashboard state
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [dashboardStats, setDashboardStats] = useState({
    totalOrders: 0,
    activeQuotes: 0,
    totalSpent: 0,
    pendingOrders: 0,
  });

  // Notification state
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const userData = localStorage.getItem("user");

    if (!token) {
      navigate("/login");
      return;
    }

    if (userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
    }
  }, [navigate]);

  // Handle URL tab parameter
  useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (
      tabParam &&
      ["dashboard", "quotes", "profile", "orders", "finance"].includes(tabParam)
    ) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  // Fetch unread notification count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const response = await notificationAPI.getUnreadCount();
        if (response.data.success) {
          setUnreadCount(response.data.data.unread_count);
        }
      } catch (error) {
        console.error("Failed to fetch unread count:", error);
      }
    };

    if (user) {
      fetchUnreadCount();
      // Refresh every 30 seconds
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  // Fetch dashboard data when dashboard tab is active
  useEffect(() => {
    if (activeTab === "dashboard") {
      fetchDashboardData();
    }
  }, [activeTab]);

  const fetchDashboardData = async () => {
    setDashboardLoading(true);
    try {
      // Fetch both quotes and orders in parallel
      const [quotesResponse, ordersResponse] = await Promise.all([
        quoteAPI.getQuotes(),
        orderAPI.getOrders(),
      ]);

      let fetchedQuotes = [];
      let fetchedOrders = [];

      if (quotesResponse.data.success) {
        fetchedQuotes = quotesResponse.data.data || [];
        setQuotes(fetchedQuotes);
      }

      if (ordersResponse.data.success) {
        fetchedOrders = ordersResponse.data.data || ordersResponse.data || [];
        setOrders(fetchedOrders);
      }

      // Calculate stats from real data
      const totalOrders = fetchedOrders.length;
      const activeQuotes = fetchedQuotes.filter(
        (q) => q.status === "pending" || q.status === "quoted",
      ).length;
      const totalSpent = fetchedOrders.reduce((sum, order) => {
        return sum + (parseFloat(order.total_amount) || 0);
      }, 0);
      const pendingOrders = fetchedOrders.filter(
        (order) =>
          order.status === "pending" ||
          order.status === "confirmed" ||
          order.status === "dispatched",
      ).length;

      setDashboardStats({
        totalOrders,
        activeQuotes,
        totalSpent,
        pendingOrders,
      });
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setDashboardLoading(false);
    }
  };

  // Fetch finance status when finance tab is active
  useEffect(() => {
    if (activeTab === "finance") {
      fetchFinanceStatus();
    }
  }, [activeTab]);

  const fetchFinanceStatus = async () => {
    try {
      const response = await financeAPI.getFinanceDocuments();
      if (response.data.success && response.data.data) {
        setFinanceStatus(response.data.data.status || "submitted");
        // Pre-populate documents if they exist
        if (response.data.data.documents) {
          setFinanceDocuments({
            bankStatement: response.data.data.documents.bank_statement
              ? { name: "Bank Statement (Uploaded)" }
              : null,
            gstCertificate: response.data.data.documents.gst_certificate
              ? { name: "GST Certificate (Uploaded)" }
              : null,
            udyamCertificate: response.data.data.documents.udyam_certificate
              ? { name: "Udyam Certificate (Uploaded)" }
              : null,
            panCard: response.data.data.documents.pan_card
              ? { name: "PAN Card (Uploaded)" }
              : null,
          });
        }
      }
    } catch (error) {
      // No documents uploaded yet - that's fine
      setFinanceStatus(null);
    }
  };

  const handleFileChange = (field, file) => {
    setFinanceDocuments((prev) => ({
      ...prev,
      [field]: file,
    }));
    setFinanceError(null);
  };

  const removeFile = (field) => {
    setFinanceDocuments((prev) => ({
      ...prev,
      [field]: null,
    }));
  };

  const handleFinanceSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!financeDocuments.bankStatement || !financeDocuments.gstCertificate) {
      setFinanceError("Bank Statement and GST Certificate are required");
      return;
    }

    setFinanceLoading(true);
    setFinanceError(null);

    try {
      const formData = new FormData();
      formData.append("bank_statement", financeDocuments.bankStatement);
      formData.append("gst_certificate", financeDocuments.gstCertificate);
      if (financeDocuments.udyamCertificate) {
        formData.append("udyam_certificate", financeDocuments.udyamCertificate);
      }
      if (financeDocuments.panCard) {
        formData.append("pan_card", financeDocuments.panCard);
      }

      const response = await financeAPI.uploadFinanceDocuments(formData);

      if (response.data.success) {
        setFinanceSuccess(true);
        setFinanceStatus("submitted");
        setTimeout(() => setFinanceSuccess(false), 5000);
      } else {
        setFinanceError(response.data.message || "Failed to upload documents");
      }
    } catch (error) {
      console.error("Error uploading finance documents:", error);
      setFinanceError(
        error.response?.data?.message ||
          "Failed to upload documents. Please try again.",
      );
    } finally {
      setFinanceLoading(false);
    }
  };

  // Fetch quotes when quotes tab is active
  useEffect(() => {
    if (activeTab === "quotes") {
      fetchQuotes();
    }
  }, [activeTab]);

  // Fetch orders when orders tab is active
  useEffect(() => {
    if (activeTab === "orders") {
      fetchOrders();
    }
  }, [activeTab]);

  const fetchQuotes = async () => {
    setQuotesLoading(true);
    setQuotesError(null);
    try {
      // Fetch both quotes and orders in parallel
      const [quotesResponse, ordersResponse] = await Promise.all([
        quoteAPI.getQuotes(),
        orderAPI.getOrders(),
      ]);

      if (quotesResponse.data.success) {
        setQuotes(quotesResponse.data.data);
      }

      if (ordersResponse.data.success) {
        setOrders(ordersResponse.data.data || ordersResponse.data);
      }
    } catch (error) {
      console.error("Error fetching quotes:", error);
      setQuotesError("Failed to load quotes");
    } finally {
      setQuotesLoading(false);
    }
  };

  const fetchOrders = async () => {
    setOrdersLoading(true);
    setOrdersError(null);
    try {
      const response = await orderAPI.getOrders();
      if (response.data.success) {
        setOrders(response.data.data || response.data);
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
      setOrdersError("Failed to load orders");
    } finally {
      setOrdersLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
  };

  // Format amount for display (lakhs/crores)
  const formatAmount = (amount) => {
    if (amount >= 10000000) {
      return `₹${(amount / 10000000).toFixed(1)}Cr`;
    } else if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(1)}L`;
    } else if (amount >= 1000) {
      return `₹${(amount / 1000).toFixed(1)}K`;
    }
    return `₹${amount}`;
  };

  // Stats data - now using dynamic values
  const stats = [
    {
      title: "Total Orders",
      value: dashboardStats.totalOrders.toString(),
      change:
        dashboardStats.totalOrders > 0
          ? `${dashboardStats.totalOrders} orders`
          : "No orders yet",
      changeType: "positive",
      icon: ShoppingCart,
      color: "blue",
      bgColor: "bg-blue-50",
      iconColor: "text-blue-600",
      borderColor: "border-blue-200",
    },
    {
      title: "Active Quotes",
      value: dashboardStats.activeQuotes.toString(),
      change:
        dashboardStats.activeQuotes > 0
          ? `${dashboardStats.activeQuotes} pending`
          : "No active quotes",
      changeType: "positive",
      icon: FileText,
      color: "secondary",
      bgColor: "bg-secondary-50",
      iconColor: "text-secondary-600",
      borderColor: "border-secondary-200",
    },
    {
      title: "Total Spent",
      value: formatAmount(dashboardStats.totalSpent),
      change: dashboardStats.totalSpent > 0 ? "All time" : "No spending yet",
      changeType: "positive",
      icon: Wallet,
      color: "green",
      bgColor: "bg-green-50",
      iconColor: "text-green-600",
      borderColor: "border-green-200",
    },
    {
      title: "Pending Orders",
      value: dashboardStats.pendingOrders.toString(),
      change:
        dashboardStats.pendingOrders > 0 ? "In progress" : "All delivered",
      changeType: dashboardStats.pendingOrders > 0 ? "neutral" : "positive",
      icon: Clock,
      color: "orange",
      bgColor: "bg-orange-50",
      iconColor: "text-orange-600",
      borderColor: "border-orange-200",
    },
  ];

  // Quick actions
  const quickActions = [
    {
      title: "Browse Products",
      description: "Explore our complete catalog",
      icon: Package,
      color: "primary",
      action: () => navigate("/products"),
    },
    {
      title: "Request Quote",
      description: "Get pricing for your requirements",
      icon: FileText,
      color: "secondary",
      action: () => setActiveTab("quotes"),
    },
    {
      title: "Track Orders",
      description: "View your order status",
      icon: Activity,
      color: "blue",
      action: () => setActiveTab("orders"),
    },
    {
      title: "Finance",
      description: "Manage payments & credit",
      icon: Wallet,
      color: "green",
      action: () => setActiveTab("finance"),
    },
  ];

  // Format date helper
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Format price helper
  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(price);
  };

  const parseSizePrices = (val) => {
    if (!val) return null;
    if (Array.isArray(val)) return val.length > 0 ? val : null;
    try {
      const a = JSON.parse(val);
      return Array.isArray(a) && a.length > 0 ? a : null;
    } catch {
      return null;
    }
  };

  const fmtInr = (n) =>
    parseFloat(n).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  // Format relative time
  const formatRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) {
      return diffMins <= 1 ? "Just now" : `${diffMins} mins ago`;
    } else if (diffHours < 24) {
      return diffHours === 1 ? "1 hour ago" : `${diffHours} hours ago`;
    } else if (diffDays < 7) {
      return diffDays === 1 ? "1 day ago" : `${diffDays} days ago`;
    } else if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return weeks === 1 ? "1 week ago" : `${weeks} weeks ago`;
    } else {
      return formatDate(dateString);
    }
  };

  // Get recent orders (max 5 from real data)
  const getRecentOrders = () => {
    return orders.slice(0, 5).map((order) => ({
      id: order.order_number || `ORD-${order.id}`,
      product: order.product?.name || "Product",
      quantity: `${order.quantity} ${order.product?.unit || "units"}`,
      status: order.status || "pending",
      date: formatRelativeTime(order.created_at),
      amount: formatPrice(order.total_amount),
    }));
  };

  // Generate recent activities from orders and quotes
  const getRecentActivities = () => {
    const activities = [];

    // Add order activities
    orders.slice(0, 3).forEach((order) => {
      let message = "";
      switch (order.status) {
        case "delivered":
          message = `Order #${order.order_number || order.id} delivered successfully`;
          break;
        case "dispatched":
          message = `Order #${order.order_number || order.id} dispatched`;
          break;
        case "confirmed":
          message = `Order #${order.order_number || order.id} confirmed`;
          break;
        default:
          message = `Order #${order.order_number || order.id} placed`;
      }
      activities.push({
        type: "order",
        message,
        time: formatRelativeTime(order.updated_at || order.created_at),
        date: new Date(order.updated_at || order.created_at),
      });
    });

    // Add quote activities
    quotes.slice(0, 2).forEach((quote) => {
      const hasResponses = quote.responses && quote.responses.length > 0;
      activities.push({
        type: "quote",
        message: hasResponses
          ? `Quote received for ${quote.product?.name || "product"}`
          : `Quote requested for ${quote.product?.name || "product"}`,
        time: formatRelativeTime(quote.updated_at || quote.created_at),
        date: new Date(quote.updated_at || quote.created_at),
      });
    });

    // Sort by date (most recent first) and return top 4
    return activities.sort((a, b) => b.date - a.date).slice(0, 4);
  };

  const recentOrders = getRecentOrders();
  const recentActivities = getRecentActivities();

  // Check if order has been placed for a quote response
  const hasOrderForResponse = (quoteId, responseId) => {
    return orders.some(
      (order) =>
        order.quote_id === quoteId ||
        (order.quote && order.quote.id === quoteId),
    );
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "delivered":
        return "bg-green-100 text-green-800 border-green-200";
      case "in-transit":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "processing":
        return "bg-orange-100 text-orange-800 border-orange-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getQuoteStatusColor = (status) => {
    switch (status) {
      case "quoted":
        return "bg-green-100 text-green-800 border-green-200";
      case "pending":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "rejected":
        return "bg-red-100 text-red-800 border-red-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case "order":
        return <ShoppingCart className="w-4 h-4" />;
      case "quote":
        return <FileText className="w-4 h-4" />;
      case "payment":
        return <Wallet className="w-4 h-4" />;
      default:
        return <Activity className="w-4 h-4" />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header/Navigation */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div>
              <img
                src="/logo.png"
                alt="Loha Mart"
                className="h-10 w-auto object-contain"
              />
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-6">
              <button
                onClick={() => navigate("/products")}
                className="text-gray-700 hover:text-primary-600 font-medium transition-colors"
              >
                Products
              </button>
              <button
                onClick={() => setActiveTab("orders")}
                className={`font-medium transition-colors ${activeTab === "orders" ? "text-primary-600" : "text-gray-700 hover:text-primary-600"}`}
              >
                Orders
              </button>
              <button
                onClick={() => setActiveTab("quotes")}
                className={`font-medium transition-colors ${activeTab === "quotes" ? "text-primary-600" : "text-gray-700 hover:text-primary-600"}`}
              >
                Quotes
              </button>
              <button
                onClick={() => setActiveTab("finance")}
                className={`font-medium transition-colors ${activeTab === "finance" ? "text-primary-600" : "text-gray-700 hover:text-primary-600"}`}
              >
                Finance
              </button>
            </nav>

            {/* Right Section */}
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate("/notifications")}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors relative"
                title="Notifications"
              >
                <Bell className="w-5 h-5 text-gray-600" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </button>

              {/* User Menu */}
              <div className="hidden md:flex items-center space-x-3 pl-3 border-l border-gray-200">
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-900">
                    {user?.customer?.company_name || "User"}
                  </p>
                  <p className="text-xs text-gray-500">{user?.mobile}</p>
                </div>
                <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-white" />
                </div>
                <button
                  onClick={handleLogout}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Logout"
                >
                  <LogOut className="w-5 h-5 text-gray-600" />
                </button>
              </div>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                {mobileMenuOpen ? (
                  <X className="w-6 h-6 text-gray-600" />
                ) : (
                  <Menu className="w-6 h-6 text-gray-600" />
                )}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-200 animate-slide-down">
              <div className="space-y-2">
                <button
                  onClick={() => {
                    navigate("/products");
                    setMobileMenuOpen(false);
                  }}
                  className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Products
                </button>
                <button
                  onClick={() => {
                    setActiveTab("orders");
                    setMobileMenuOpen(false);
                  }}
                  className={`block w-full text-left px-4 py-2 hover:bg-gray-100 rounded-lg transition-colors ${activeTab === "orders" ? "text-primary-600 bg-primary-50" : "text-gray-700"}`}
                >
                  Orders
                </button>
                <button
                  onClick={() => {
                    setActiveTab("quotes");
                    setMobileMenuOpen(false);
                  }}
                  className={`block w-full text-left px-4 py-2 hover:bg-gray-100 rounded-lg transition-colors ${activeTab === "quotes" ? "text-primary-600 bg-primary-50" : "text-gray-700"}`}
                >
                  Quotes
                </button>
                <button
                  onClick={() => {
                    setActiveTab("finance");
                    setMobileMenuOpen(false);
                  }}
                  className={`block w-full text-left px-4 py-2 hover:bg-gray-100 rounded-lg transition-colors ${activeTab === "finance" ? "text-primary-600 bg-primary-50" : "text-gray-700"}`}
                >
                  Finance
                </button>
                <button
                  onClick={() => {
                    navigate("/notifications");
                    setMobileMenuOpen(false);
                  }}
                  className="flex w-full items-center justify-between px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <div className="flex items-center space-x-2">
                    <Bell className="w-5 h-5" />
                    <span>Notifications</span>
                  </div>
                  {unreadCount > 0 && (
                    <span className="w-6 h-6 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                      {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                  )}
                </button>
                <button
                  onClick={handleLogout}
                  className="block w-full text-left px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  Logout
                </button>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Incomplete Profile Banner */}
        {!user?.customer && (
          <div className="mb-6 bg-gradient-to-r from-orange-50 to-yellow-50 border-2 border-orange-200 rounded-lg p-4 animate-slide-down">
            <div className="flex items-start gap-3">
              <div className="bg-orange-500 p-2 rounded-lg flex-shrink-0">
                <AlertCircle className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-orange-900 mb-1">
                  Complete Your Profile
                </h3>
                <p className="text-sm text-orange-800 mb-3">
                  Your profile is incomplete. Complete it now to unlock full
                  access to quotes, orders, and premium features.
                </p>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => navigate("/register")}
                  leftIcon={<User className="w-4 h-4" />}
                >
                  Complete Profile Now
                </Button>
              </div>
              <button
                onClick={() => {
                  /* You could add state to dismiss this banner */
                }}
                className="text-orange-600 hover:text-orange-800 transition-colors flex-shrink-0"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Tabs Navigation */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
              Welcome back, {user?.customer?.company_name || "User"}!
            </h2>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/register")}
              leftIcon={<Edit className="w-4 h-4" />}
            >
              Edit Profile
            </Button>
          </div>

          {/* Tab Buttons */}
          <div className="border-b border-gray-200">
            <nav className="flex space-x-4 sm:space-x-8 overflow-x-auto scrollbar-hide pb-px">
              <button
                onClick={() => setActiveTab("dashboard")}
                className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm transition-colors flex-shrink-0 ${
                  activeTab === "dashboard"
                    ? "border-primary-600 text-primary-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <div className="flex items-center space-x-2">
                  <Activity className="w-4 h-4" />
                  <span>Dashboard</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab("profile")}
                className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm transition-colors flex-shrink-0 ${
                  activeTab === "profile"
                    ? "border-primary-600 text-primary-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <div className="flex items-center space-x-2">
                  <User className="w-4 h-4" />
                  <span>Profile</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab("quotes")}
                className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm transition-colors flex-shrink-0 ${
                  activeTab === "quotes"
                    ? "border-primary-600 text-primary-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <div className="flex items-center space-x-2">
                  <FileText className="w-4 h-4" />
                  <span>My Quotes</span>
                  {quotes.filter((q) => q.status === "quoted").length > 0 && (
                    <Badge variant="primary" size="sm">
                      {quotes.filter((q) => q.status === "quoted").length}
                    </Badge>
                  )}
                </div>
              </button>
              <button
                onClick={() => setActiveTab("orders")}
                className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm transition-colors flex-shrink-0 ${
                  activeTab === "orders"
                    ? "border-primary-600 text-primary-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <div className="flex items-center space-x-2">
                  <ShoppingCart className="w-4 h-4" />
                  <span>My Orders</span>
                  {orders.length > 0 && (
                    <Badge variant="success" size="sm">
                      {orders.length}
                    </Badge>
                  )}
                </div>
              </button>
              <button
                onClick={() => setActiveTab("finance")}
                className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm transition-colors flex-shrink-0 ${
                  activeTab === "finance"
                    ? "border-primary-600 text-primary-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <div className="flex items-center space-x-2">
                  <CreditCard className="w-4 h-4" />
                  <span>Finance</span>
                  {financeStatus === "verified" && (
                    <Badge variant="success" size="sm">
                      <CheckCircle className="w-3 h-3" />
                    </Badge>
                  )}
                </div>
              </button>
            </nav>
          </div>
        </div>

        {/* Dashboard View */}
        {activeTab === "dashboard" && (
          <div className="animate-fade-in">
            <p className="text-gray-600 mb-8">
              Here's what's happening with your business today.
            </p>

            {/* Loading State */}
            {dashboardLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
              </div>
            ) : (
              <>
                {/* Stats Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                  {stats.map((stat, index) => {
                    const Icon = stat.icon;
                    return (
                      <Card
                        key={index}
                        className={`hover:shadow-lg transition-all duration-300 cursor-pointer border ${stat.borderColor} animate-scale-up`}
                        style={{ animationDelay: `${index * 0.1}s` }}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-600 mb-1">
                              {stat.title}
                            </p>
                            <p className="text-2xl font-bold text-gray-900 mb-2">
                              {dashboardLoading ? "..." : stat.value}
                            </p>
                            <div className="flex items-center space-x-1">
                              <span
                                className={`text-xs font-medium ${
                                  stat.changeType === "positive"
                                    ? "text-green-600"
                                    : stat.changeType === "neutral"
                                      ? "text-orange-600"
                                      : "text-gray-600"
                                }`}
                              >
                                {stat.change}
                              </span>
                            </div>
                          </div>
                          <div className={`${stat.bgColor} p-3 rounded-lg`}>
                            <Icon className={`w-6 h-6 ${stat.iconColor}`} />
                          </div>
                        </div>
                      </Card>
                    );
                  })}
                </div>

                {/* Quick Actions */}
                <div className="mb-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">
                    Quick Actions
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {quickActions.map((action, index) => {
                      const Icon = action.icon;
                      return (
                        <Card
                          key={index}
                          className="hover:shadow-lg transition-all duration-300 cursor-pointer group"
                          onClick={action.action}
                        >
                          <div className="flex items-start space-x-4">
                            <div
                              className={`bg-${action.color}-50 p-3 rounded-lg group-hover:scale-110 transition-transform duration-300`}
                            >
                              <Icon
                                className={`w-6 h-6 text-${action.color}-600`}
                              />
                            </div>
                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-900 mb-1">
                                {action.title}
                              </h4>
                              <p className="text-sm text-gray-600">
                                {action.description}
                              </p>
                            </div>
                            <ChevronRight className="w-5 h-5 text-gray-400 group-hover:translate-x-1 transition-transform duration-300" />
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                </div>

                {/* Two Column Layout */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Recent Orders */}
                  <div className="lg:col-span-2">
                    <Card>
                      <div className="flex items-center justify-between mb-6">
                        <h3 className="text-xl font-bold text-gray-900">
                          Recent Orders
                        </h3>
                        <button
                          onClick={() => navigate("/orders")}
                          className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center space-x-1"
                        >
                          <span>View All</span>
                          <ArrowRight className="w-4 h-4" />
                        </button>
                      </div>

                      <div className="space-y-4">
                        {recentOrders.map((order, index) => (
                          <div
                            key={index}
                            className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                          >
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-center gap-2 mb-2">
                                <h4 className="font-semibold text-gray-900 truncate">
                                  {order.product}
                                </h4>
                                <span
                                  className={`text-xs px-2 py-1 rounded-full border font-medium whitespace-nowrap ${getStatusColor(
                                    order.status,
                                  )}`}
                                >
                                  {order.status.replace("-", " ")}
                                </span>
                              </div>
                              <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600">
                                <span className="flex items-center space-x-1">
                                  <Package className="w-4 h-4 flex-shrink-0" />
                                  <span>{order.quantity}</span>
                                </span>
                                <span className="flex items-center space-x-1">
                                  <Clock className="w-4 h-4 flex-shrink-0" />
                                  <span>{order.date}</span>
                                </span>
                              </div>
                            </div>
                            <div className="sm:text-right">
                              <p className="text-sm text-gray-500 mb-1">
                                {order.id}
                              </p>
                              <p className="font-bold text-gray-900">
                                {order.amount}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>

                      {recentOrders.length === 0 && (
                        <div className="text-center py-12">
                          <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                          <p className="text-gray-500 mb-4">No orders yet</p>
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => navigate("/products")}
                          >
                            Browse Products
                          </Button>
                        </div>
                      )}
                    </Card>
                  </div>

                  {/* Recent Activity */}
                  <div className="lg:col-span-1">
                    <Card>
                      <h3 className="text-xl font-bold text-gray-900 mb-6">
                        Recent Activity
                      </h3>

                      {recentActivities.length > 0 ? (
                        <div className="space-y-4">
                          {recentActivities.map((activity, index) => (
                            <div
                              key={index}
                              className="flex items-start space-x-3"
                            >
                              <div className="bg-primary-50 p-2 rounded-lg mt-1">
                                <div className="text-primary-600">
                                  {getActivityIcon(activity.type)}
                                </div>
                              </div>
                              <div className="flex-1">
                                <p className="text-sm text-gray-900 font-medium">
                                  {activity.message}
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                  {activity.time}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <Activity className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                          <p className="text-gray-500 text-sm">
                            No recent activity
                          </p>
                        </div>
                      )}
                    </Card>

                    {/* Help Card */}
                    <Card className="mt-6 bg-gradient-to-br from-primary-50 to-secondary-50 border-2 border-primary-200">
                      <div className="text-center">
                        <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-3">
                          <AlertCircle className="w-6 h-6 text-primary-600" />
                        </div>
                        <h4 className="font-bold text-gray-900 mb-2">
                          Need Help?
                        </h4>
                        <p className="text-sm text-gray-600 mb-4">
                          Our support team is here to assist you
                        </p>
                        <Button
                          variant="primary"
                          size="sm"
                          fullWidth
                          onClick={() => navigate("/contact")}
                        >
                          Contact Support
                        </Button>
                      </div>
                    </Card>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Profile View */}
        {activeTab === "profile" && (
          <div className="animate-fade-in">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Profile Card */}
              <div className="lg:col-span-2">
                <Card>
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-bold text-gray-900">
                      Company Profile
                    </h3>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate("/register")}
                      leftIcon={<Edit className="w-4 h-4" />}
                    >
                      Edit
                    </Button>
                  </div>

                  <div className="space-y-6">
                    {/* Company Information */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-500 uppercase mb-4">
                        Company Information
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-start space-x-3">
                          <div className="bg-primary-50 p-2 rounded-lg mt-1">
                            <Building2 className="w-5 h-5 text-primary-600" />
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">
                              Company Name
                            </p>
                            <p className="font-semibold text-gray-900">
                              {user?.customer?.company_name || "N/A"}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-start space-x-3">
                          <div className="bg-secondary-50 p-2 rounded-lg mt-1">
                            <User className="w-5 h-5 text-secondary-600" />
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">
                              Contact Person
                            </p>
                            <p className="font-semibold text-gray-900">
                              {user?.customer?.contact_person || "N/A"}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Contact Information */}
                    <div className="pt-6 border-t border-gray-200">
                      <h4 className="text-sm font-semibold text-gray-500 uppercase mb-4">
                        Contact Information
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-start space-x-3">
                          <div className="bg-blue-50 p-2 rounded-lg mt-1">
                            <Phone className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">
                              Mobile Number
                            </p>
                            <p className="font-semibold text-gray-900">
                              {user?.mobile || "N/A"}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-start space-x-3">
                          <div className="bg-green-50 p-2 rounded-lg mt-1">
                            <Mail className="w-5 h-5 text-green-600" />
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">
                              Email Address
                            </p>
                            <p className="font-semibold text-gray-900">
                              {user?.email || user?.customer?.email || "N/A"}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Tax Information */}
                    <div className="pt-6 border-t border-gray-200">
                      <h4 className="text-sm font-semibold text-gray-500 uppercase mb-4">
                        Tax Information
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-start space-x-3">
                          <div className="bg-orange-50 p-2 rounded-lg mt-1">
                            <FileText className="w-5 h-5 text-orange-600" />
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">GSTIN</p>
                            <p className="font-semibold text-gray-900">
                              {user?.customer?.gstin || "Not Provided"}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-start space-x-3">
                          <div className="bg-purple-50 p-2 rounded-lg mt-1">
                            <FileText className="w-5 h-5 text-purple-600" />
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">PAN</p>
                            <p className="font-semibold text-gray-900">
                              {user?.customer?.pan || "Not Provided"}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Address Information */}
                    <div className="pt-6 border-t border-gray-200">
                      <h4 className="text-sm font-semibold text-gray-500 uppercase mb-4">
                        Address
                      </h4>
                      <div className="flex items-start space-x-3">
                        <div className="bg-red-50 p-2 rounded-lg mt-1">
                          <MapPin className="w-5 h-5 text-red-600" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-gray-500 mb-1">
                            Business Address
                          </p>
                          {user?.customer?.address ? (
                            <div className="font-semibold text-gray-900 space-y-1">
                              <p>{user.customer.address}</p>
                              <p>
                                {[
                                  user.customer.city,
                                  user.customer.state,
                                  user.customer.pincode,
                                ]
                                  .filter(Boolean)
                                  .join(", ")}
                              </p>
                            </div>
                          ) : (
                            <p className="font-semibold text-gray-900">
                              Not Provided
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>

              {/* Stats Card */}
              <div className="lg:col-span-1 space-y-6">
                <Card className="bg-gradient-to-br from-primary-50 to-secondary-50 border-2 border-primary-200">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-primary-600 to-secondary-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <User className="w-8 h-8 text-white" />
                    </div>
                    <h4 className="font-bold text-gray-900 text-lg mb-1">
                      {user?.customer?.company_name || "User"}
                    </h4>
                    <p className="text-sm text-gray-600 mb-4">
                      Customer Account
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Member Since</span>
                        <span className="font-semibold text-gray-900">
                          {user?.customer?.created_at
                            ? formatDate(user.customer.created_at)
                            : "N/A"}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Total Orders</span>
                        <span className="font-semibold text-gray-900">
                          {dashboardStats.totalOrders}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Total Spent</span>
                        <span className="font-semibold text-gray-900">
                          {formatAmount(dashboardStats.totalSpent)}
                        </span>
                      </div>
                    </div>
                  </div>
                </Card>

                <Card>
                  <h4 className="font-bold text-gray-900 mb-4">
                    Account Status
                  </h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">
                        Profile Completed
                      </span>
                      <Badge variant="success">100%</Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">
                        Verification
                      </span>
                      <Badge variant="success">Verified</Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">
                        Account Type
                      </span>
                      <Badge>Business</Badge>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        )}

        {/* Quotes View */}
        {activeTab === "quotes" && (
          <div className="animate-fade-in">
            <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900">
                  My Quote Requests
                </h3>
                <p className="text-gray-600 mt-1">
                  View all your product quote requests and supplier responses
                </p>
              </div>
              <Button
                variant="primary"
                onClick={() => navigate("/quotes/request")}
                leftIcon={<FileText className="w-4 h-4" />}
              >
                New Quote Request
              </Button>
            </div>

            {/* Quote Requests List */}
            <div className="space-y-6">
              {quotesLoading ? (
                <Card>
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading quotes...</p>
                  </div>
                </Card>
              ) : quotesError ? (
                <Card>
                  <div className="text-center py-12">
                    <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
                    <p className="text-red-600 font-semibold mb-2">
                      Failed to load quotes
                    </p>
                    <p className="text-gray-600 text-sm mb-4">{quotesError}</p>
                    <Button variant="primary" onClick={fetchQuotes}>
                      Try Again
                    </Button>
                  </div>
                </Card>
              ) : quotes.length === 0 ? (
                <Card>
                  <div className="text-center py-12">
                    <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h4 className="text-lg font-bold text-gray-900 mb-2">
                      No Quote Requests Yet
                    </h4>
                    <p className="text-gray-600 mb-6">
                      Start by browsing products and requesting quotes
                    </p>
                    <Button
                      variant="primary"
                      onClick={() => navigate("/products")}
                    >
                      Browse Products
                    </Button>
                  </div>
                </Card>
              ) : (
                quotes.map((quote) => (
                  <Card
                    key={quote.id}
                    className="hover:shadow-lg transition-shadow"
                  >
                    <div className="space-y-4">
                      {/* Quote Header */}
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <h4 className="text-lg font-bold text-gray-900 truncate">
                              {quote.product?.name || "Product"}
                            </h4>
                            <span
                              className={`text-xs px-2 py-1 rounded-full border font-medium whitespace-nowrap ${getQuoteStatusColor(quote.status)}`}
                            >
                              {quote.status === "quoted"
                                ? "Quotes Received"
                                : quote.status === "pending"
                                  ? "Pending"
                                  : quote.status}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600">
                            <span className="flex items-center space-x-1">
                              <Package className="w-4 h-4 flex-shrink-0" />
                              <span>
                                {quote.quantity} {quote.unit}
                              </span>
                            </span>
                            <span className="flex items-center space-x-1">
                              <Calendar className="w-4 h-4 flex-shrink-0" />
                              <span>
                                Requested: {formatDate(quote.created_at)}
                              </span>
                            </span>
                            <span className="text-gray-500">
                              #{quote.quote_number}
                            </span>
                          </div>
                        </div>
                        {quote.responses && quote.responses.length > 0 && (
                          <div className="bg-green-50 px-3 py-1 rounded-full flex-shrink-0">
                            <span className="text-sm font-semibold text-green-700">
                              {quote.responses.length}{" "}
                              {quote.responses.length === 1
                                ? "Quote"
                                : "Quotes"}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Quote Description */}
                      {quote.customer_notes && (
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <p className="text-sm text-gray-700">
                            <span className="font-semibold">Notes:</span>{" "}
                            {quote.customer_notes}
                          </p>
                        </div>
                      )}

                      {/* Supplier Responses */}
                      {quote.responses && quote.responses.length > 0 ? (
                        <div className="space-y-3">
                          <h5 className="font-semibold text-gray-900 flex items-center space-x-2">
                            <MessageSquare className="w-4 h-4" />
                            <span>Supplier Quotes</span>
                          </h5>
                          {quote.responses.map((response) => (
                            <div
                              key={response.id}
                              className="bg-gradient-to-r from-primary-50 to-secondary-50 border-2 border-primary-100 rounded-lg p-4"
                            >
                              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-3">
                                <div>
                                  <h6 className="font-bold text-gray-900">
                                    {response.supplier?.company_name ||
                                      "Admin Quote"}
                                  </h6>
                                  <p className="text-xs text-gray-500">
                                    Quoted on {formatDate(response.created_at)}
                                  </p>
                                </div>
                                <div className="sm:text-right">
                                  <p className="text-2xl font-bold text-primary-700">
                                    {formatPrice(response.total_price)}
                                  </p>
                                  <p className="text-sm text-gray-600">
                                    {formatPrice(response.price_per_unit)}/
                                    {quote.unit}
                                  </p>
                                </div>
                              </div>

                              {response.remarks && (
                                <div className="mb-3">
                                  <p className="text-sm text-gray-700">
                                    <span className="font-semibold">
                                      Remarks:
                                    </span>{" "}
                                    {response.remarks}
                                  </p>
                                </div>
                              )}

                              <div className="flex items-center space-x-2 text-sm text-gray-600 mb-3">
                                <Clock className="w-4 h-4" />
                                <span>
                                  Valid for {response.validity_days || 30} days
                                </span>
                              </div>

                              <div className="flex items-center space-x-3">
                                {hasOrderForResponse(quote.id, response.id) ? (
                                  <div className="flex items-center space-x-2 px-4 py-2 bg-green-100 text-green-700 rounded-lg border border-green-300">
                                    <CheckCircle className="w-4 h-4" />
                                    <span className="text-sm font-semibold">
                                      Order Placed
                                    </span>
                                  </div>
                                ) : (
                                  <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={() =>
                                      navigate(
                                        `/place-order?response=${response.id}`,
                                      )
                                    }
                                  >
                                    Place Order
                                  </Button>
                                )}
                                <Button
                                  variant="outline"
                                  size="sm"
                                  leftIcon={<Eye className="w-4 h-4" />}
                                  onClick={() =>
                                    setQuoteDetailModal({ quote, response })
                                  }
                                >
                                  View Details
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                          <Clock className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
                          <p className="text-sm font-semibold text-yellow-800">
                            Waiting for Supplier Quotes
                          </p>
                          <p className="text-xs text-yellow-700 mt-1">
                            We'll notify you once suppliers respond to your
                            request
                          </p>
                        </div>
                      )}
                    </div>
                  </Card>
                ))
              )}
            </div>
          </div>
        )}

        {/* Orders View */}
        {activeTab === "orders" && (
          <div className="animate-fade-in">
            <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900">My Orders</h3>
                <p className="text-gray-600 mt-1">
                  Track and manage all your orders
                </p>
              </div>
              <Button
                variant="primary"
                onClick={() => navigate("/products")}
                leftIcon={<Package className="w-4 h-4" />}
              >
                Browse Products
              </Button>
            </div>

            {/* Orders List */}
            <div className="space-y-6">
              {ordersLoading ? (
                <Card>
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading orders...</p>
                  </div>
                </Card>
              ) : ordersError ? (
                <Card>
                  <div className="text-center py-12">
                    <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
                    <p className="text-red-600 font-semibold mb-2">
                      Failed to load orders
                    </p>
                    <p className="text-gray-600 text-sm mb-4">{ordersError}</p>
                    <Button variant="primary" onClick={fetchOrders}>
                      Try Again
                    </Button>
                  </div>
                </Card>
              ) : orders.length === 0 ? (
                <Card>
                  <div className="text-center py-12">
                    <ShoppingCart className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h4 className="text-lg font-bold text-gray-900 mb-2">
                      No Orders Yet
                    </h4>
                    <p className="text-gray-600 mb-6">
                      Start by browsing products and requesting quotes
                    </p>
                    <Button
                      variant="primary"
                      onClick={() => navigate("/products")}
                    >
                      Browse Products
                    </Button>
                  </div>
                </Card>
              ) : (
                orders.map((order) => (
                  <Card
                    key={order.id}
                    className="hover:shadow-lg transition-shadow"
                  >
                    <div className="space-y-4">
                      {/* Order Header */}
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <h4 className="text-lg font-bold text-gray-900 truncate">
                              {order.product?.name || "Product"}
                            </h4>
                            <span
                              className={`text-xs px-2 py-1 rounded-full border font-medium whitespace-nowrap ${getStatusColor(order.status)}`}
                            >
                              {order.status?.charAt(0).toUpperCase() +
                                order.status?.slice(1) || "Pending"}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600">
                            <span className="flex items-center space-x-1">
                              <Package className="w-4 h-4 flex-shrink-0" />
                              <span>
                                {order.quantity}{" "}
                                {order.product?.unit || "units"}
                              </span>
                            </span>
                            <span className="flex items-center space-x-1">
                              <Calendar className="w-4 h-4 flex-shrink-0" />
                              <span>
                                Ordered: {formatDate(order.created_at)}
                              </span>
                            </span>
                            <span className="text-gray-500">
                              #{order.order_number}
                            </span>
                          </div>
                        </div>
                        <div className="sm:text-right">
                          <p className="text-sm text-gray-500 mb-1">
                            Total Amount
                          </p>
                          <p className="text-2xl font-bold text-primary-600">
                            {formatPrice(order.total_amount)}
                          </p>
                        </div>
                      </div>

                      {/* Order Details */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                        <div>
                          <p className="text-sm text-gray-500 mb-1">Supplier</p>
                          <p className="font-semibold text-gray-900">
                            {order.supplier?.company_name || "Loha Mart"}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500 mb-1">
                            Unit Price
                          </p>
                          <p className="font-semibold text-gray-900">
                            {formatPrice(order.unit_price)}
                          </p>
                        </div>
                      </div>

                      {/* Delivery Address */}
                      {order.delivery_address && (
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <div className="flex items-start space-x-2">
                            <MapPin className="w-4 h-4 text-gray-500 mt-0.5" />
                            <div>
                              <p className="text-sm font-semibold text-gray-700 mb-1">
                                Delivery Address
                              </p>
                              <p className="text-sm text-gray-600">
                                {order.delivery_address}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Order Actions */}
                      <div className="flex flex-wrap items-center justify-between gap-3 pt-4 border-t border-gray-200">
                        <div className="flex flex-wrap items-center gap-2">
                          {order.invoice_url && (
                            <Button
                              variant="outline"
                              size="sm"
                              leftIcon={<FileText className="w-4 h-4" />}
                              onClick={() =>
                                window.open(order.invoice_url, "_blank")
                              }
                            >
                              View Invoice
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            leftIcon={<Eye className="w-4 h-4" />}
                            onClick={() => navigate(`/orders/${order.id}`)}
                          >
                            View Details
                          </Button>
                        </div>
                        {order.quote_id && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setActiveTab("quotes");
                              navigate("/dashboard?tab=quotes");
                            }}
                          >
                            View Quote
                          </Button>
                        )}
                      </div>
                    </div>
                  </Card>
                ))
              )}
            </div>
          </div>
        )}

        {/* Finance View */}
        {activeTab === "finance" && (
          <div className="animate-fade-in">
            <div className="mb-6">
              <h3 className="text-xl font-bold text-gray-900">
                Finance Documents
              </h3>
              <p className="text-gray-600 mt-1">
                Upload required documents for credit line approval (One-time
                submission)
              </p>
            </div>

            {/* Status Banner */}
            {financeStatus === "verified" && (
              <div className="mb-6 bg-green-50 border-2 border-green-200 rounded-lg p-4 animate-slide-down">
                <div className="flex items-center space-x-3">
                  <div className="bg-green-500 p-2 rounded-lg">
                    <CheckCircle className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-green-900">
                      Documents Verified
                    </h4>
                    <p className="text-sm text-green-700">
                      Your finance documents have been verified. You are
                      eligible for credit line.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {financeStatus === "submitted" && (
              <div className="mb-6 bg-blue-50 border-2 border-blue-200 rounded-lg p-4 animate-slide-down">
                <div className="flex items-center space-x-3">
                  <div className="bg-blue-500 p-2 rounded-lg">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-blue-900">
                      Documents Under Review
                    </h4>
                    <p className="text-sm text-blue-700">
                      Your documents have been submitted and are being reviewed.
                      We'll notify you once verified.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {financeSuccess && (
              <div className="mb-6 bg-green-50 border-2 border-green-200 rounded-lg p-4 animate-slide-down">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                  <p className="font-semibold text-green-700">
                    Documents uploaded successfully!
                  </p>
                </div>
              </div>
            )}

            {financeError && (
              <div className="mb-6 bg-red-50 border-2 border-red-200 rounded-lg p-4 animate-slide-down">
                <div className="flex items-center space-x-3">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                  <p className="font-semibold text-red-700">{financeError}</p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Document Upload Form */}
              <div className="lg:col-span-2">
                <Card>
                  <form onSubmit={handleFinanceSubmit}>
                    <h4 className="text-lg font-bold text-gray-900 mb-6">
                      Required Documents
                    </h4>

                    <div className="space-y-6">
                      {/* Bank Statement */}
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-primary-400 transition-colors">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start space-x-4">
                            <div className="bg-blue-50 p-3 rounded-lg">
                              <FileText className="w-6 h-6 text-blue-600" />
                            </div>
                            <div>
                              <h5 className="font-semibold text-gray-900">
                                Bank Statement{" "}
                                <span className="text-red-500">*</span>
                              </h5>
                              <p className="text-sm text-gray-500 mt-1">
                                Last 6 months bank statement (PDF format)
                              </p>
                              {financeDocuments.bankStatement && (
                                <div className="flex items-center space-x-2 mt-2 text-green-600">
                                  <FileCheck className="w-4 h-4" />
                                  <span className="text-sm font-medium">
                                    {financeDocuments.bankStatement.name}
                                  </span>
                                  {financeStatus !== "submitted" &&
                                    financeStatus !== "verified" && (
                                      <button
                                        type="button"
                                        onClick={() =>
                                          removeFile("bankStatement")
                                        }
                                        className="text-red-500 hover:text-red-700"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    )}
                                </div>
                              )}
                            </div>
                          </div>
                          {!financeStatus && (
                            <label className="cursor-pointer">
                              <input
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png"
                                className="hidden"
                                onChange={(e) =>
                                  handleFileChange(
                                    "bankStatement",
                                    e.target.files[0],
                                  )
                                }
                              />
                              <div className="flex items-center space-x-2 px-4 py-2 bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100 transition-colors">
                                <Upload className="w-4 h-4" />
                                <span className="text-sm font-medium">
                                  Upload
                                </span>
                              </div>
                            </label>
                          )}
                        </div>
                      </div>

                      {/* GST Certificate */}
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-primary-400 transition-colors">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start space-x-4">
                            <div className="bg-green-50 p-3 rounded-lg">
                              <FileText className="w-6 h-6 text-green-600" />
                            </div>
                            <div>
                              <h5 className="font-semibold text-gray-900">
                                GST Certificate{" "}
                                <span className="text-red-500">*</span>
                              </h5>
                              <p className="text-sm text-gray-500 mt-1">
                                Updated GST registration certificate
                              </p>
                              {financeDocuments.gstCertificate && (
                                <div className="flex items-center space-x-2 mt-2 text-green-600">
                                  <FileCheck className="w-4 h-4" />
                                  <span className="text-sm font-medium">
                                    {financeDocuments.gstCertificate.name}
                                  </span>
                                  {financeStatus !== "submitted" &&
                                    financeStatus !== "verified" && (
                                      <button
                                        type="button"
                                        onClick={() =>
                                          removeFile("gstCertificate")
                                        }
                                        className="text-red-500 hover:text-red-700"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    )}
                                </div>
                              )}
                            </div>
                          </div>
                          {!financeStatus && (
                            <label className="cursor-pointer">
                              <input
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png"
                                className="hidden"
                                onChange={(e) =>
                                  handleFileChange(
                                    "gstCertificate",
                                    e.target.files[0],
                                  )
                                }
                              />
                              <div className="flex items-center space-x-2 px-4 py-2 bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100 transition-colors">
                                <Upload className="w-4 h-4" />
                                <span className="text-sm font-medium">
                                  Upload
                                </span>
                              </div>
                            </label>
                          )}
                        </div>
                      </div>

                      {/* Optional Documents Section */}
                      <h4 className="text-lg font-bold text-gray-900 mt-8 mb-4">
                        Optional Documents
                      </h4>

                      {/* Udyam Certificate */}
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-primary-400 transition-colors">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start space-x-4">
                            <div className="bg-purple-50 p-3 rounded-lg">
                              <FileText className="w-6 h-6 text-purple-600" />
                            </div>
                            <div>
                              <h5 className="font-semibold text-gray-900">
                                Udyam Certificate{" "}
                                <span className="text-gray-400 text-sm font-normal">
                                  (Optional)
                                </span>
                              </h5>
                              <p className="text-sm text-gray-500 mt-1">
                                MSME/Udyam registration certificate
                              </p>
                              {financeDocuments.udyamCertificate && (
                                <div className="flex items-center space-x-2 mt-2 text-green-600">
                                  <FileCheck className="w-4 h-4" />
                                  <span className="text-sm font-medium">
                                    {financeDocuments.udyamCertificate.name}
                                  </span>
                                  {financeStatus !== "submitted" &&
                                    financeStatus !== "verified" && (
                                      <button
                                        type="button"
                                        onClick={() =>
                                          removeFile("udyamCertificate")
                                        }
                                        className="text-red-500 hover:text-red-700"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    )}
                                </div>
                              )}
                            </div>
                          </div>
                          {!financeStatus && (
                            <label className="cursor-pointer">
                              <input
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png"
                                className="hidden"
                                onChange={(e) =>
                                  handleFileChange(
                                    "udyamCertificate",
                                    e.target.files[0],
                                  )
                                }
                              />
                              <div className="flex items-center space-x-2 px-4 py-2 bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100 transition-colors">
                                <Upload className="w-4 h-4" />
                                <span className="text-sm font-medium">
                                  Upload
                                </span>
                              </div>
                            </label>
                          )}
                        </div>
                      </div>

                      {/* PAN Card */}
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-primary-400 transition-colors">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start space-x-4">
                            <div className="bg-orange-50 p-3 rounded-lg">
                              <CreditCard className="w-6 h-6 text-orange-600" />
                            </div>
                            <div>
                              <h5 className="font-semibold text-gray-900">
                                PAN Card{" "}
                                <span className="text-gray-400 text-sm font-normal">
                                  (Optional)
                                </span>
                              </h5>
                              <p className="text-sm text-gray-500 mt-1">
                                Company or Proprietor PAN card copy
                              </p>
                              {financeDocuments.panCard && (
                                <div className="flex items-center space-x-2 mt-2 text-green-600">
                                  <FileCheck className="w-4 h-4" />
                                  <span className="text-sm font-medium">
                                    {financeDocuments.panCard.name}
                                  </span>
                                  {financeStatus !== "submitted" &&
                                    financeStatus !== "verified" && (
                                      <button
                                        type="button"
                                        onClick={() => removeFile("panCard")}
                                        className="text-red-500 hover:text-red-700"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    )}
                                </div>
                              )}
                            </div>
                          </div>
                          {!financeStatus && (
                            <label className="cursor-pointer">
                              <input
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png"
                                className="hidden"
                                onChange={(e) =>
                                  handleFileChange("panCard", e.target.files[0])
                                }
                              />
                              <div className="flex items-center space-x-2 px-4 py-2 bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100 transition-colors">
                                <Upload className="w-4 h-4" />
                                <span className="text-sm font-medium">
                                  Upload
                                </span>
                              </div>
                            </label>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Submit Button */}
                    {!financeStatus && (
                      <div className="mt-8 pt-6 border-t border-gray-200">
                        <Button
                          type="submit"
                          variant="primary"
                          size="lg"
                          fullWidth
                          loading={financeLoading}
                          disabled={
                            !financeDocuments.bankStatement ||
                            !financeDocuments.gstCertificate
                          }
                        >
                          Submit Documents for Verification
                        </Button>
                        <p className="text-xs text-gray-500 mt-3 text-center">
                          * Bank Statement and GST Certificate are required
                        </p>
                      </div>
                    )}
                  </form>
                </Card>
              </div>

              {/* Info Card */}
              <div className="lg:col-span-1 space-y-6">
                <Card className="bg-gradient-to-br from-primary-50 to-secondary-50 border-2 border-primary-200">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-primary-600 to-secondary-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Wallet className="w-8 h-8 text-white" />
                    </div>
                    <h4 className="font-bold text-gray-900 text-lg mb-2">
                      Credit Line Benefits
                    </h4>
                    <p className="text-sm text-gray-600 mb-4">
                      Get approved for a credit line and unlock these benefits
                    </p>
                    <div className="space-y-3 text-left">
                      <div className="flex items-start space-x-3">
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-700">
                          Buy now, pay later option
                        </span>
                      </div>
                      <div className="flex items-start space-x-3">
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-700">
                          Net 30/60 payment terms
                        </span>
                      </div>
                      <div className="flex items-start space-x-3">
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-700">
                          Priority order processing
                        </span>
                      </div>
                      <div className="flex items-start space-x-3">
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-700">
                          Exclusive bulk discounts
                        </span>
                      </div>
                    </div>
                  </div>
                </Card>

                <Card>
                  <h4 className="font-bold text-gray-900 mb-4">
                    Document Guidelines
                  </h4>
                  <div className="space-y-3 text-sm text-gray-600">
                    <div className="flex items-start space-x-2">
                      <span className="text-primary-600 font-bold">1.</span>
                      <span>Documents should be clear and readable</span>
                    </div>
                    <div className="flex items-start space-x-2">
                      <span className="text-primary-600 font-bold">2.</span>
                      <span>Accepted formats: PDF, JPG, PNG</span>
                    </div>
                    <div className="flex items-start space-x-2">
                      <span className="text-primary-600 font-bold">3.</span>
                      <span>Maximum file size: 5MB per document</span>
                    </div>
                    <div className="flex items-start space-x-2">
                      <span className="text-primary-600 font-bold">4.</span>
                      <span>Bank statement must be latest 6 months</span>
                    </div>
                  </div>
                </Card>

                <Card>
                  <h4 className="font-bold text-gray-900 mb-4">Need Help?</h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Contact our finance team for assistance with document
                    submission.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    fullWidth
                    onClick={() => navigate("/contact")}
                    leftIcon={<Phone className="w-4 h-4" />}
                  >
                    Contact Support
                  </Button>
                </Card>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* ── Quote Detail Modal ── */}
      {quoteDetailModal &&
        (() => {
          const { quote, response } = quoteDetailModal;
          const sizePrices = parseSizePrices(response.size_prices);
          const isAdmin = response.quoted_by === "admin";
          const sourceName = isAdmin
            ? "lohamart (Admin)"
            : response.supplier?.company_name || "Supplier";
          const selectedItems = (() => {
            try {
              return Array.isArray(quote.selected_items)
                ? quote.selected_items
                : JSON.parse(quote.selected_items || "[]");
            } catch {
              return [];
            }
          })();
          const selectedBrands = (() => {
            try {
              return Array.isArray(quote.selected_brands)
                ? quote.selected_brands
                : JSON.parse(quote.selected_brands || "[]");
            } catch {
              return [];
            }
          })();

          return (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              {/* Overlay */}
              <div
                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                onClick={() => setQuoteDetailModal(null)}
              />

              {/* Modal Panel */}
              <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
                {/* Modal Header */}
                <div
                  className={`px-6 py-4 flex items-center justify-between ${isAdmin ? "bg-primary-700" : "bg-secondary-600"}`}
                >
                  <div>
                    <h3 className="text-lg font-bold text-white">
                      Quote Details
                    </h3>
                    <p className="text-sm text-white/80">
                      {quote.product?.name} · #{quote.quote_number}
                    </p>
                  </div>
                  <button
                    onClick={() => setQuoteDetailModal(null)}
                    className="text-white/80 hover:text-white transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                {/* Scrollable Body */}
                <div className="overflow-y-auto flex-1 p-6 space-y-5">
                  {/* Source + Validity + Total */}
                  <div className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3 border border-gray-200">
                    <div>
                      <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide">
                        Quoted By
                      </p>
                      <p className="font-bold text-gray-900 mt-0.5">
                        {sourceName}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        on {formatDate(response.created_at)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide">
                        Total Amount
                      </p>
                      <p
                        className={`text-2xl font-extrabold mt-0.5 ${isAdmin ? "text-primary-700" : "text-secondary-700"}`}
                      >
                        ₹{fmtInr(response.total_price)}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Valid for {response.validity_days || 30} days
                      </p>
                    </div>
                  </div>

                  {/* Size-wise Price Breakdown */}
                  {sizePrices ? (
                    <div>
                      <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">
                        Price Breakdown by Size
                      </p>
                      <div className="rounded-xl border border-gray-200 overflow-hidden">
                        <table className="w-full text-sm">
                          <thead>
                            <tr
                              className={`border-b border-gray-200 ${isAdmin ? "bg-primary-50" : "bg-secondary-50"}`}
                            >
                              {sizePrices.some((sp) => sp.product_name) && (
                                <th className="text-left px-4 py-2.5 text-xs font-bold text-gray-600 uppercase tracking-wide">
                                  Product
                                </th>
                              )}
                              {sizePrices.some((sp) => sp.brand_name) && (
                                <th className="text-left px-4 py-2.5 text-xs font-bold text-gray-600 uppercase tracking-wide">
                                  Brand
                                </th>
                              )}
                              <th className="text-left px-4 py-2.5 text-xs font-bold text-gray-600 uppercase tracking-wide">
                                Size / Item
                              </th>
                              <th className="text-center px-4 py-2.5 text-xs font-bold text-gray-600 uppercase tracking-wide">
                                Qty
                              </th>
                              <th className="text-right px-4 py-2.5 text-xs font-bold text-gray-600 uppercase tracking-wide">
                                Price / Unit
                              </th>
                              <th className="text-right px-4 py-2.5 text-xs font-bold text-gray-600 uppercase tracking-wide">
                                Amount
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {sizePrices.map((sp, i) => {
                              const rowTotal =
                                parseFloat(sp.total) ||
                                parseFloat(sp.price_per_unit) *
                                  parseFloat(sp.quantity);
                              const hasProductCol = sizePrices.some((s) => s.product_name);
                              const hasBrandCol = sizePrices.some((s) => s.brand_name);
                              return (
                                <tr key={i} className="hover:bg-gray-50/60">
                                  {hasProductCol && (
                                    <td className="px-4 py-3 font-semibold text-gray-800">
                                      {sp.product_name || '—'}
                                    </td>
                                  )}
                                  {hasBrandCol && (
                                    <td className="px-4 py-3 font-semibold text-gray-800">
                                      {sp.brand_name || '—'}
                                    </td>
                                  )}
                                  <td className="px-4 py-3 font-semibold text-gray-800">
                                    {sp.size}
                                  </td>
                                  <td className="px-4 py-3 text-center text-gray-600">
                                    {sp.quantity}
                                  </td>
                                  <td className="px-4 py-3 text-right text-gray-700">
                                    ₹{fmtInr(sp.price_per_unit)}
                                  </td>
                                  <td className="px-4 py-3 text-right font-semibold text-gray-800">
                                    ₹{fmtInr(rowTotal)}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                          <tfoot>
                            <tr
                              className={`border-t-2 ${isAdmin ? "border-primary-200 bg-primary-50" : "border-secondary-200 bg-secondary-50"}`}
                            >
                              <td
                                colSpan={3}
                                className="px-4 py-3 font-bold text-gray-800"
                              >
                                Grand Total
                              </td>
                              <td
                                className={`px-4 py-3 text-right font-extrabold text-base ${isAdmin ? "text-primary-700" : "text-secondary-700"}`}
                              >
                                ₹{fmtInr(response.total_price)}
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3 border border-gray-200">
                      <span className="text-sm text-gray-600">
                        Price per unit
                      </span>
                      <span className="font-bold text-gray-800">
                        ₹{fmtInr(response.price_per_unit)} / {quote.unit}
                      </span>
                    </div>
                  )}

                  {/* Remarks */}
                  {response.remarks && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                      <p className="text-xs font-bold text-amber-700 uppercase tracking-wide mb-1">
                        Remarks
                      </p>
                      <p className="text-sm text-gray-700">
                        {response.remarks}
                      </p>
                    </div>
                  )}

                  {/* Items You Requested */}
                  {selectedItems.length > 0 && (
                    <div>
                      <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">
                        Items You Requested
                      </p>
                      <div className="rounded-xl border border-gray-200 overflow-hidden">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-gray-50 border-b border-gray-200">
                              <th className="text-left px-3 py-2 text-xs text-gray-500 font-semibold">
                                Size
                              </th>
                              <th className="text-right px-3 py-2 text-xs text-gray-500 font-semibold">
                                Qty Requested
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {selectedItems.map((item, i) => (
                              <tr key={i} className="hover:bg-gray-50/60">
                                <td className="px-3 py-2 text-gray-700 font-medium">
                                  {item.size}
                                </td>
                                <td className="px-3 py-2 text-right text-gray-600">
                                  {item.quantity}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Brands */}
                  {selectedBrands.length > 0 && (
                    <div className="flex flex-wrap gap-2 items-center">
                      <span className="text-xs font-semibold text-gray-500">
                        Preferred Brands:
                      </span>
                      {selectedBrands.map((b, i) => (
                        <span
                          key={i}
                          className="flex items-center gap-1.5 text-xs bg-secondary-50 text-secondary-700 border border-secondary-200 px-2.5 py-1 rounded-full font-medium"
                        >
                          {b.logo_url && (
                            <img
                              src={b.logo_url}
                              alt={b.brand_name}
                              className="w-4 h-4 object-contain rounded"
                            />
                          )}
                          {b.brand_name}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Customer Notes */}
                  {quote.customer_notes && (
                    <p className="text-xs text-gray-500 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                      <span className="font-semibold text-amber-700">
                        Your Note:{" "}
                      </span>
                      {quote.customer_notes}
                    </p>
                  )}
                </div>

                {/* Modal Footer */}
                <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-between items-center gap-3">
                  <button
                    onClick={() => setQuoteDetailModal(null)}
                    className="px-4 py-2 text-sm font-semibold text-gray-600 hover:text-gray-900 transition-colors"
                  >
                    Close
                  </button>
                  {!hasOrderForResponse(quote.id, response.id) && (
                    <button
                      onClick={() => {
                        setQuoteDetailModal(null);
                        navigate(`/place-order?response=${response.id}`);
                      }}
                      className="flex items-center gap-2 px-5 py-2.5 bg-green-600 hover:bg-green-700 text-white text-sm font-bold rounded-xl transition-colors shadow-sm"
                    >
                      <ShoppingCart className="w-4 h-4" />
                      Place Order
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })()}
    </div>
  );
};

export default Home;
